// --- Imports ---
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import ReactJoyride, { EVENTS } from 'react-joyride'
import { useTranslation } from 'react-i18next'
import './ProductTour.scss'
import { useLocation, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'redux/reducers/rootReducer'
import {
    SetRunAction,
    SetStepAction,
    SetStepIndexAction,
} from 'containers/app/store/actions'
import {
    PATH_AUTH,
    PATH_FEATURES,
    DESIGN_ROOT,
    PATH_BRANDING,
    PATH_DESIGN,
    PATH_SETTINGS,
    PATH_BUILD,
    PATH_NEW_LOADER,
} from '../../config/paths'
import { GuideTooltip } from './guide-tooltip/GuideTooltip'
import { getSteps } from './getSteps'

const spotLightPaddingIndexes = [6, 10, 12, 19]
const excludedPaths = [
    PATH_AUTH.login,
    PATH_AUTH.registration,
    PATH_AUTH.sendResetPassword,
    PATH_AUTH.resetPassword,
]

function waitForElement(selector: string, timeout = 3000): Promise<void> {
    return new Promise((resolve, reject) => {
        const interval = 50
        let elapsed = 0
        const check = () => {
            if (document.querySelector(selector)) resolve()
            else if (elapsed >= timeout)
                reject(new Error(`Element not found: ${selector}`))
            else {
                elapsed += interval
                setTimeout(check, interval)
            }
        }
        check()
    })
}

const ProductTour = () => {
    const location = useLocation()
    const dispatch = useDispatch()
    const { t } = useTranslation('productTour')
    const { push } = useHistory()
    const [windowWidth, setWindowWidth] = useState(window.innerWidth)
    const [applyMargins, setApplyMargins] = useState(false)
    const { runProductTour, stepIndex } = useSelector(
        (state: RootState) => state.app,
    )
    const steps = useMemo(() => getSteps(t, windowWidth), [t, windowWidth])

    useEffect(() => {
        if (
            !localStorage.getItem('guide') &&
            location.pathname.includes(PATH_FEATURES.root)
        ) {
            dispatch(SetRunAction(true))
        }
    }, [location.pathname, dispatch])

    // Always start tour for DEVELOPMENT
    // useEffect(() => {
    //     dispatch(SetRunAction(true))
    //     localStorage.setItem('guide', 'run')
    //     dispatch(SetStepIndexAction(0))
    // }, [])

    useEffect(() => {
        // Window resize handler
        const handleResize = () => setWindowWidth(window.innerWidth)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        dispatch(SetStepAction(steps))
    }, [steps, dispatch])

    const handleTourStepChange = useCallback(
        (data: any) => {
            const { action, type, index } = data
            if (type === EVENTS.STEP_AFTER) {
                const nextIndex = index + 1
                if (index === steps.length - 1) {
                    dispatch(SetRunAction(false))
                    dispatch(SetStepIndexAction(-1))
                    localStorage.setItem('guide', 'finished')
                    localStorage.removeItem('tourStepIndex')
                    return
                }
                const asyncSteps: Record<
                    number,
                    { selector: string; path?: string }
                > = {
                    5: { selector: '#seventh-step' },
                    6: {
                        selector: '#generalDesign',
                        path: PATH_DESIGN.colorPalette,
                    },
                    15: { selector: '#generalSettings' },
                    18: { selector: '.build-actions__buttons' },
                    21: { selector: '.new-loader__wrapper' },
                }

                if (asyncSteps[index]) {
                    waitForElement(asyncSteps[index].selector).then(() => {
                        dispatch(SetStepIndexAction(nextIndex))
                    })
                    if (asyncSteps[index].path) push(asyncSteps[index].path)
                } else {
                    dispatch(SetStepIndexAction(nextIndex))
                }

                localStorage.setItem('tourStepIndex', nextIndex.toString())

                const routeMap: Record<number, string> = {
                    0: PATH_FEATURES.booking.root,
                    5: PATH_BRANDING.root,
                    7: PATH_DESIGN.background,
                    8: PATH_DESIGN.launchScreen,
                    10: PATH_DESIGN.icon,
                    12: PATH_FEATURES.booking.root,
                    15: PATH_SETTINGS.appSettings.general,
                    16: PATH_SETTINGS.featuresActivation.root,
                    17: PATH_SETTINGS.userRoles.root,
                    18: PATH_BUILD.android,
                    20: PATH_NEW_LOADER.root,
                }
                if (routeMap[index]) push(routeMap[index])
                if (index === 0 && location.pathname.includes(DESIGN_ROOT))
                    setApplyMargins(true)
            }
            if (action === 'skip') {
                dispatch(SetRunAction(false))
                dispatch(SetStepIndexAction(-1))
                localStorage.setItem('guide', 'skipped')
                localStorage.removeItem('tourStepIndex')
            }
        },
        [dispatch, push, location.pathname, steps.length],
    )

    if (excludedPaths.includes(location.pathname)) return null

    return runProductTour && (stepIndex > -1 || stepIndex < 18) ? (
        <div className='product-tour__wrapper'>
            <ReactJoyride
                run={runProductTour}
                stepIndex={stepIndex}
                steps={steps}
                callback={handleTourStepChange}
                disableScrolling
                continuous
                showSkipButton
                styles={{
                    overlay: { backgroundColor: 'rgba(217, 217, 217)' },
                    spotlight: {
                        marginTop: stepIndex === 6 ? 0 : applyMargins ? 80 : 0,
                    },
                }}
                floaterProps={{ hideArrow: true, disableAnimation: false }}
                disableOverlayClose
                spotlightPadding={
                    spotLightPaddingIndexes.includes(stepIndex) ? 20 : 0
                }
                tooltipComponent={GuideTooltip}
            />
            <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(-10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
        </div>
    ) : null
}

export default ProductTour
